/**
 * Payment Result Block
 *
 * Unified result page for both deposit and withdrawal flows.
 *
 * This page serves two journeys, and which one it is decides everything below.
 *
 * **In the cashier's window.** The parent injects an iframe, the PSP runs the
 * payment inside it and navigates that iframe to this page. The page above is
 * still the cashier, so the flow can be read from it, and the parent is notified
 * by postMessage when the result lands.
 *
 * **Having left the site.** The player went to the provider's own page and came
 * back here at top level. There is no page above to read, no parent to notify,
 * and the return address is the same for both flows and every player — so the
 * flow has to be carried across, which the submit handler does (a cookie the
 * server reads before this renders, mirrored into browser storage). See
 * modules/payment-journey.js.
 *
 * Flow detection, in order. The first to answer wins:
 *   0. data-journey-flow — the fork, resolved by the server. Set only for a
 *      journey that left the site, which is also the only case where every
 *      mechanism below has nothing to read.
 *   1. window.top.location.pathname contains 'deposit' or 'withdraw' — the
 *      cashier's own address, readable because it is the same origin.
 *   2. URL hash (#deposit / #withdrawal) — set by the parent on iframe.src.
 *   3. the mirror in browser storage, consulted only at top level: inside the
 *      cashier's window the page above is the authority, and a leftover from an
 *      abandoned payment must not outrank it.
 *   4. postMessage from the parent on every iframe 'load' event.
 *   5. neutral — generic "Your transaction" copy after 300 ms.
 *
 * Flow values:
 *   'deposit'    – deposit_complete / deposit_reopen postMessages
 *   'withdrawal' – withdrawal_complete / withdrawal_reopen postMessages
 *   'neutral'    – no postMessage sent; generic "Your transaction" copy shown
 *
 * Provider return values (Status, OrderReference, Pspid) come from data
 * attributes the server filled in, not from the query string: the provider may
 * return the player by POST, and a browser cannot read the body of the request
 * that produced its own document. The query string stays as a fallback.
 *
 * @package betbeast
 */

import { readJourney, clearJourney } from '../modules/payment-journey.js';

/**
 * Whether this page is the top window.
 *
 * Comparing the two references is safe across origins; reading a property of
 * window.top would not be. A refused comparison is treated as being framed —
 * the cautious answer, since it only withholds the fallbacks.
 *
 * @returns {boolean}
 */
function isTopLevel() {
    try {
        return window.top === window.self;
    } catch ( e ) {
        return false;
    }
}

document.addEventListener( 'DOMContentLoaded', () => {
    const block = document.querySelector( '[data-payment-result]' );
    if ( ! block ) return;

    // -------------------------------------------------------------------------
    // 1. Read PSP URL params and show the correct state panel immediately —
    //    this is independent of flow and can happen right away.
    // -------------------------------------------------------------------------
    const params = new URLSearchParams( window.location.search );

    /**
     * A return value: what the server read from the request, or the query string.
     *
     * The server is asked first because it can see a POST body and this page cannot.
     * The query string remains as a fallback for markup that arrives without the
     * attributes for any reason — the same values, by the only route available here.
     */
    const readValue = ( attribute, key ) => {
        const fromServer = block.dataset[ attribute ];

        if ( fromServer !== undefined && '' !== fromServer ) {
            return fromServer;
        }

        return params.get( key ) || '';
    };

    const status         = readValue( 'resultStatus', 'Status' ).toUpperCase();
    const orderReference = readValue( 'resultReference', 'OrderReference' );
    const pspid          = readValue( 'resultPspid', 'Pspid' );

    let stateKey;
    if ( status === 'PROCESSED' || status === 'SUCCESS' ) {
        stateKey = 'success';
    } else if ( status === 'PENDING' ) {
        stateKey = 'pending';
    } else {
        stateKey = 'error';
    }

    const stateEl = block.querySelector( `[data-result-state="${ stateKey }"]` );
    if ( stateEl ) {
        stateEl.style.display = 'flex';

        // Populate details table.
        const detailMap = {
            status:    status         || '—',
            reference: orderReference || '—',
            pspid:     pspid          || '—',
        };
        Object.entries( detailMap ).forEach( ( [ key, value ] ) => {
            const row = stateEl.querySelector( `[data-detail="${ key }"] [data-detail-value]` );
            if ( row ) row.textContent = value;
        } );

        // Hide rows with no data.
        stateEl.querySelectorAll( '[data-detail]' ).forEach( row => {
            const val = row.querySelector( '[data-detail-value]' );
            if ( val && val.textContent === '—' ) row.style.display = 'none';
        } );
    }

    // -------------------------------------------------------------------------
    // 2. Flow resolution — synchronous where possible.
    // -------------------------------------------------------------------------
    let flow          = 'neutral';
    let flowConfirmed = false;

    /**
     * Apply the resolved flow: show elements for this flow, hide all others,
     * and send the parent complete notification exactly once.
     */
    function applyFlow( resolvedFlow ) {
        if ( flowConfirmed ) return; // already resolved — ignore duplicates
        flowConfirmed = true;
        flow          = resolvedFlow;

        block.querySelectorAll( '[data-flow-neutral]' ).forEach( el => {
            el.style.display = ( flow === 'neutral' ) ? '' : 'none';
        } );
        block.querySelectorAll( '[data-flow-deposit]' ).forEach( el => {
            el.style.display = ( flow === 'deposit' ) ? '' : 'none';
        } );
        block.querySelectorAll( '[data-flow-withdrawal]' ).forEach( el => {
            el.style.display = ( flow === 'withdrawal' ) ? '' : 'none';
        } );

        sendCompleteMessage();
    }

    /**
     * Notify the parent modal that the result page has loaded.
     * Only sent for known flows — neutral has no parent modal to notify.
     */
    function sendCompleteMessage() {
        // At top level the parent is this window, so there is nobody to tell — the
        // page carries its own navigation instead.
        if ( isTopLevel() ) {
            return;
        }

        const completeType = flow === 'withdrawal' ? 'withdrawal_complete'
                           : flow === 'deposit'    ? 'deposit_complete'
                           : null;
        if ( ! completeType ) return;
        try {
            window.parent.postMessage(
                { type: completeType, status: stateKey, orderReference: orderReference },
                '*'
            );
        } catch ( e ) {
            // Cross-origin guard — safe to ignore.
        }
    }

    // --- 0: the fork, resolved by the server before this page rendered ---
    // Present only for a journey that left the site. Its presence is the mode: a
    // payment that stayed in the cashier writes no fork, so nothing here fires and
    // the mechanisms below behave exactly as they always have.
    const serverFlow = block.dataset.journeyFlow || '';

    if ( serverFlow ) {
        // The server has consumed the cookie; drop the mirror with it so an
        // abandoned journey cannot colour a later visit.
        clearJourney();
        applyFlow( serverFlow );
    }

    // --- 1: window.top.location (same-origin, synchronous, no race condition) ---
    try {
        const parentPath = window.top.location.pathname;
        if ( parentPath.includes( 'deposit' ) ) {
            applyFlow( 'deposit' );
        } else if ( parentPath.includes( 'withdrawal' ) || parentPath.includes( 'withdraw' ) ) {
            applyFlow( 'withdrawal' );
        }
    } catch ( e ) {
        // Cross-origin — window.top not accessible; fall through to next signals.
    }

    // --- 2: URL hash (set by parent on initial iframe.src) ---
    if ( ! flowConfirmed ) {
        const hash = ( window.location.hash || '' ).replace( '#', '' ).toLowerCase();
        if ( hash === 'deposit' || hash === 'withdrawal' ) {
            applyFlow( hash );
        }
    }

    // --- 3: the mirror in browser storage ---
    // For a return where the cookie did not survive the trip: whether one does is
    // the browser's decision, taken on rules that differ between browsers. Storage
    // is never sent with a request, so none of that applies to it.
    //
    // Only at top level. Inside the cashier's window the page above is the
    // authority, and a leftover from an abandoned payment must not outrank it.
    if ( ! flowConfirmed && isTopLevel() ) {
        const mirrored = readJourney();

        if ( mirrored ) {
            clearJourney();
            applyFlow( mirrored.flow );
        }
    }

    // --- 4: postMessage from parent on every iframe 'load' event ---
    window.addEventListener( 'message', ( event ) => {
        if ( ! event.data || event.data.type !== 'payment_result_init' ) return;
        const msgFlow = ( event.data.flow || '' ).toLowerCase();
        if ( msgFlow === 'deposit' || msgFlow === 'withdrawal' ) {
            applyFlow( msgFlow );
        }
    } );

    // --- 5: neutral after 300 ms if no signal arrives ---
    setTimeout( () => {
        if ( ! flowConfirmed ) applyFlow( 'neutral' );
    }, 300 );

    // -------------------------------------------------------------------------
    // 3. "Try Again" button — reopens the parent modal.
    //    Uses current value of `flow` at click time (may have been updated by
    //    postMessage after initial render).
    // -------------------------------------------------------------------------
    const tryAgainBtn = block.querySelector( '[data-try-again]' );
    if ( tryAgainBtn ) {
        tryAgainBtn.addEventListener( 'click', () => {
            // No parent to reopen: the player came back from the provider's own page
            // and this button reached them because the server had no fork to render a
            // link from — the flow was resolved after render, from browser storage.
            // Without this the button would post a message to its own window and do
            // nothing at all.
            if ( isTopLevel() ) {
                const target = 'withdrawal' === flow
                    ? block.dataset.cashierWithdrawal
                    : block.dataset.cashierDeposit;

                if ( target ) {
                    window.location.assign( target );
                    return;
                }
            }

            if ( flow === 'withdrawal' ) {
                try { window.parent.postMessage( { type: 'withdrawal_reopen' }, '*' ); } catch ( e ) { window.parent.history.back(); }
            } else if ( flow === 'deposit' ) {
                try { window.parent.postMessage( { type: 'deposit_reopen' }, '*' ); } catch ( e ) { window.parent.history.back(); }
            } else {
                try { window.parent.history.back(); } catch ( e ) { window.history.back(); }
            }
        } );
    }
} );
